/** Observes updates without reloading a form, clearing auth, or replacing an installation. */
export function watchUpdates(
  registration: Pick<ServiceWorkerRegistration, 'installing' | 'waiting' | 'update' | 'addEventListener' | 'removeEventListener'>,
  container: ServiceWorkerContainer,
  onReady: () => void,
  page: Document = document,
  host: Window = window,
): () => void {
  let disposed = false
  let checking = false
  let hadController = container.controller !== null
  const workers = new Set<ServiceWorker>()
  function controllerChanged() {
    if (hadController && !disposed) onReady()
    hadController = container.controller !== null
  }
  function changed() {
    if (registration.waiting !== null && hadController && !disposed) onReady()
  }
  function found() {
    const worker = registration.installing
    if (worker === null || workers.has(worker)) return
    workers.add(worker)
    worker.addEventListener('statechange', changed)
  }
  async function check() {
    if (disposed || checking || page.visibilityState !== 'visible' || !host.navigator.onLine) return
    checking = true
    try { await registration.update(); changed() } catch {
      // Offline/CDN failures must never hide the working application or discard a draft.
    } finally { checking = false }
  }
  container.addEventListener('controllerchange', controllerChanged)
  registration.addEventListener('updatefound', found)
  page.addEventListener('visibilitychange', check)
  host.addEventListener('online', check)
  found()
  changed()
  void check()
  const timer = host.setInterval(() => { void check() }, 120_000)
  return () => {
    disposed = true
    host.clearInterval(timer)
    container.removeEventListener('controllerchange', controllerChanged)
    registration.removeEventListener('updatefound', found)
    page.removeEventListener('visibilitychange', check)
    host.removeEventListener('online', check)
    for (const worker of workers) worker.removeEventListener('statechange', changed)
  }
}
